import React, { useCallback, useMemo } from "react"
import {
  Block,
  Copy03,
  InfoCircle,
  LabelMedium,
  LinkExternal02,
  ListItem,
  ListItemLabel,
  useStyletron,
} from "@mezo-org/mezo-clay"

export type MarketTransactionDetailsItemProps = {
  label: string
  value?: string | null
  type: "clipboard" | "external-link" | "info-link" | "text"
  onClick?: () => void
}

export default function MarketTransactionDetailsItem({
  label,
  value,
  type,
  onClick,
}: MarketTransactionDetailsItemProps) {
  const [, theme] = useStyletron()

  const Icon = useMemo(() => {
    switch (type) {
      case "clipboard":
        return Copy03
      case "external-link":
        return LinkExternal02
      case "info-link":
        return InfoCircle
      default:
        return null
    }
  }, [type])

  const [copiedMessage, setCopiedMessage] = React.useState<string>()

  const handleOnClick = useCallback(() => {
    if (!onClick) return

    onClick()

    if (type === "clipboard") {
      setCopiedMessage("Copied to clipboard")

      setTimeout(() => {
        setCopiedMessage(undefined)
      }, 2000)
    }
  }, [onClick, type])

  const itemValue = useMemo(() => {
    if (copiedMessage) return copiedMessage

    if (value === null) return "Please try again later"
    if (value === undefined) return "Loading..."
    return value
  }, [value, copiedMessage])

  return (
    <ListItem
      endEnhancer={() => (
        <Block display="flex" alignItems="center">
          <LabelMedium
            color={theme.colors.contentSecondary}
            marginRight={Icon ? theme.sizing.scale700 : 0}
          >
            {itemValue}
          </LabelMedium>

          {Icon && <Icon color={theme.colors.contentPrimary} size={24} />}
        </Block>
      )}
      onClick={handleOnClick}
      overrides={{
        Content: {
          style: {
            borderBottom: "none",
            minHeight: "auto",
          },
        },
      }}
    >
      <ListItemLabel
        overrides={{
          LabelContent: {
            style: {
              color: theme.colors.contentPrimary,
            },
          },
        }}
      >
        {label}
      </ListItemLabel>
    </ListItem>
  )
}
